import { CdeMilestoneLink } from './CdeMilestoneLink';
import { useMemo } from 'react';
import { CheckSquare, Ban, X } from 'lucide-react';
import type { Project, Task } from '../types';
import { useT } from '../i18n';
import { getProjectMilestones, getMilestoneProgress, getMilestoneState } from '../utils/milestoneProgress';
import { getUrgency } from '../utils/echeances';
import { fmtShortDate } from '../utils/date';
import { projectDisplayCode } from '../utils/identifiers';
import { parseDeliverables, formatMilestoneDueDate } from './ProjectForm';

// Just the task fields the open-tasks list needs — lets callers pass either
// full board Tasks (Gérance) or the lighter per-project task list (Tasks page).
export type MilestonePanelTask = Pick<Task, 'id' | 'title' | 'project_id' | 'milestone_id' | 'column_id' | 'blocked_reason' | 'assignee_name' | 'due_date'>;

function goToProject(id: number) {
  window.location.hash = `project?id=${id}`;
}

function goToTask(id: number) {
  window.location.hash = `tasks?task=${id}`;
}

// Formats an ISO timestamp as e.g. "18 août 2026 14:32" (see DateHistory).
function formatChangedAt(iso: string): string {
  const d = new Date(iso);
  return d.toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' }) +
    ' ' + d.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' });
}

// One date field's change list within the detail panel — used for both
// start_date_history and due_date_history, which render identically aside
// from the field label prefixing the section heading.
function MilestoneHistorySection({ label, history }: {
  label: string;
  history: { old_date: string | null; new_date: string | null; changed_at: string; changed_by: string }[];
}) {
  const { t } = useT();
  return (
    <div className="detail-section">
      <div className="detail-label">{label} — {t('milestones.dueDateHistoryToggle', { n: history.length })}</div>
      <ul className="ms-due-history-list">
        {[...history].reverse().map((h, i) => (
          <li key={i}>
            {t('milestones.dueDateHistoryEntry', {
              old: h.old_date ? formatMilestoneDueDate(h.old_date) : t('milestones.dueDateHistoryNone'),
              new: h.new_date ? formatMilestoneDueDate(h.new_date) : t('milestones.dueDateHistoryNone'),
              date: formatChangedAt(h.changed_at),
              who: h.changed_by,
            })}
          </li>
        ))}
      </ul>
    </div>
  );
}

// Side panel with the full milestone record (state, dates, description,
// assignments, acceptance criteria, open tasks, start/due-date history).
// Opened from a Gérance Gantt bar's "!" overlay and from a task's milestone
// field. `project` must carry `planning` (and `deliverables` for the linked
// deliverables line); `tasks` may hold any tasks — only this milestone's
// unfinished ones are listed.
export function MilestoneDetailPanel({ project, milestoneId, tasks, doneColumnIds, blockedColumnIds, onClose }: {
  project: Pick<Project, 'id' | 'name' | 'project_code' | 'planning' | 'deliverables'>;
  milestoneId: string;
  tasks: MilestonePanelTask[];
  doneColumnIds: Set<number>;
  blockedColumnIds: Set<number>;
  onClose: () => void;
}) {
  const { t } = useT();
  const milestones = useMemo(() => getProjectMilestones(project), [project]);
  const index = milestones.findIndex(m => m.id === milestoneId);
  const milestone = index >= 0 ? milestones[index] : undefined;
  const openTasks = useMemo(
    () => tasks.filter(task => task.project_id === project.id && task.milestone_id === milestoneId && !doneColumnIds.has(task.column_id)),
    [tasks, project.id, milestoneId, doneColumnIds]
  );
  const deliverables = useMemo(() => parseDeliverables(project.deliverables), [project]);
  const linkedDeliverables = useMemo(
    () => milestone?.acceptance_deliverables?.length ? deliverables.filter(d => milestone.acceptance_deliverables!.includes(d.id)) : [],
    [milestone, deliverables]
  );

  const state = milestone ? getMilestoneState(milestone, index, getMilestoneProgress(milestones)) : 'pending';
  const urgency = state === 'active' && milestone?.estimated_end ? getUrgency(milestone.estimated_end) : null;
  const stateLabelKey = state === 'done' ? 'gerancemeta.milestoneStateDone'
    : state === 'pending' ? 'gerancemeta.milestoneStatePending'
    : urgency === 'overdue' ? 'gerancemeta.milestoneUrgencyRed'
    : urgency === 'soon' ? 'gerancemeta.milestoneUrgencyOrange'
    : 'gerancemeta.milestoneUrgencyGreen';
  const stateBadgeClass = state === 'done' ? 'badge-accent'
    : state === 'pending' ? 'badge-plain'
    : urgency === 'overdue' ? 'badge-err'
    : urgency === 'soon' ? 'badge-warn'
    : 'badge-ok';

  const openProject = () => { goToProject(project.id); onClose(); };

  return (
    <div className="panel-overlay open" onClick={onClose}>
      <div className="panel-overlay-bg" />
      <div className="panel-slide" onClick={e => e.stopPropagation()}>
        <div className="panel-slide-head">
          <div className="panel-slide-title">{milestone?.name || t('milestones.untitled')}</div>
          <button className="panel-slide-close" onClick={onClose}><X size={16} /></button>
        </div>
        <div className="panel-slide-body">
          {milestone?.type==='process' && <CdeMilestoneLink projectId={project.id} milestoneId={milestoneId} canEdit={false}/>}
          <div className="detail-badges">
            <span className={`badge ${stateBadgeClass}`}>{t(stateLabelKey)}</span>
          </div>

          <div className="detail-section">
            <div className="detail-label">{t('echeances.col.project')}</div>
            <div className="detail-text" style={{ cursor: 'pointer' }} onClick={openProject}>
              {project.name}
              <span className="gerance-meta-card-code"> — {projectDisplayCode(project as Project)}</span>
            </div>
          </div>

          <div className="detail-section">
            <div className="detail-label">{t('milestones.estimatedStart')} / {t('milestones.estimatedEnd')}</div>
            <div className="detail-text">{t('gerancemeta.milestoneDates', {
              start: milestone?.estimated_start ? fmtShortDate(milestone.estimated_start) : '—',
              end: milestone?.estimated_end ? fmtShortDate(milestone.estimated_end) : '—',
            })}</div>
          </div>

          <div className="detail-section">
            <div className="detail-label">{t('common.description')}</div>
            <div className="detail-text">{milestone?.contents || t('gerancemeta.milestoneNoDescription')}</div>
          </div>

          {milestone?.assignments && milestone.assignments.length > 0 && (
            <div className="detail-section">
              <div className="detail-label">{t('milestones.whoDoesWhat')}</div>
              {milestone.assignments.map((a, i) => (
                <div key={i} className="detail-text">
                  <strong>{a.name}</strong>{a.note ? ` — ${a.note}` : ''}
                </div>
              ))}
            </div>
          )}

          {(milestone?.acceptance || linkedDeliverables.length > 0) && (
            <div className="detail-section">
              <div className="detail-label">{t('milestones.acceptance')}</div>
              {milestone?.acceptance && <div className="detail-text">{milestone.acceptance}</div>}
              {linkedDeliverables.length > 0 && (
                <div className="text-sm text-ink-3" style={{ marginTop: 'var(--s8)' }}>
                  {t('milestones.linkedDeliverables')} {linkedDeliverables.map(d => d.title || t('milestones.untitled')).join(', ')}
                </div>
              )}
            </div>
          )}

          <div className="detail-section">
            <div className="detail-label">{t('gerancemeta.milestoneOpenTasks')} ({openTasks.length})</div>
            {openTasks.length === 0 ? (
              <div className="text-sm text-ink-3" style={{ fontStyle: 'italic' }}>{t('gerancemeta.milestoneNoOpenTasks')}</div>
            ) : openTasks.map(task => {
              const taskBlocked = blockedColumnIds.has(task.column_id);
              return (
                <div
                  key={task.id}
                  className={`gerance-meta-task-row${taskBlocked ? ' is-blocked' : ''}`}
                  title={taskBlocked ? (task.blocked_reason || t('gerancemeta.taskBlocked')) : undefined}
                  onClick={() => goToTask(task.id)}
                >
                  {taskBlocked
                    ? <Ban size={12} className="icon-inline" style={{ color: 'var(--err)' }} />
                    : <CheckSquare size={12} className="icon-inline" />}
                  <span className="gerance-meta-task-title">
                    {task.title}
                    {task.assignee_name && <span className="gerance-meta-task-meta"> – {task.assignee_name}</span>}
                    {task.due_date && <span className="gerance-meta-task-meta"> – {fmtShortDate(task.due_date)}</span>}
                  </span>
                </div>
              );
            })}
          </div>

          {milestone?.start_date_history && milestone.start_date_history.length > 0 && (
            <MilestoneHistorySection label={t('milestones.estimatedStart')} history={milestone.start_date_history} />
          )}

          {milestone?.due_date_history && milestone.due_date_history.length > 0 && (
            <MilestoneHistorySection label={t('milestones.estimatedEnd')} history={milestone.due_date_history} />
          )}

          <div className="detail-section">
            <button type="button" className="btn btn-accent btn-sm" onClick={openProject}>{t('gerancemeta.viewProject')}</button>
          </div>
        </div>
      </div>
    </div>
  );
}
